import React, { useEffect, useRef } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronRight,
  Info,
  LoaderCircle,
  X,
} from "lucide-react";
import type { Diagnostic, LossReport } from "../../shared/contracts";

export function Button({
  children,
  variant = "",
  icon: Icon,
  busy = false,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: string;
  icon?: React.ElementType;
  busy?: boolean;
}) {
  return (
    <button
      {...props}
      className={`button ${variant} ${props.className || ""}`}
      disabled={props.disabled || busy}
    >
      {busy ? (
        <LoaderCircle size={15} className="spin" />
      ) : Icon ? (
        <Icon size={15} />
      ) : null}
      {children}
    </button>
  );
}
export function IconButton({
  label,
  icon: Icon,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  icon: React.ElementType;
}) {
  return (
    <button
      {...props}
      className={`icon-button ${props.className || ""}`}
      aria-label={label}
      title={label}
    >
      <Icon size={17} />
    </button>
  );
}
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) &&
        typeof child.type === "string" &&
        ["input", "textarea", "select"].includes(child.type)
          ? React.cloneElement(child as React.ReactElement<any>, {
              "aria-label": (child.props as any)["aria-label"] || label,
            })
          : child,
      )}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function Modal({
  title,
  subtitle,
  children,
  footer,
  onClose,
  wide = false,
  dismissible = true,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
  dismissible?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  const dismissRef = useRef(dismissible);
  closeRef.current = onClose;
  dismissRef.current = dismissible;
  useEffect(() => {
    const old = document.activeElement as HTMLElement | null;
    const box = ref.current;
    const focusable = () =>
      Array.from(
        box?.querySelectorAll<HTMLElement>(
          'button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]',
        ) || [],
      );
    focusable()[0]?.focus();
    const handle = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissRef.current) closeRef.current();
      if (e.key === "Tab") {
        const els = focusable(),
          first = els[0],
          last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    box?.addEventListener("keydown", handle);
    return () => {
      box?.removeEventListener("keydown", handle);
      old?.focus();
    };
  }, []);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) =>
        e.target === e.currentTarget && dismissible && onClose()
      }
    >
      <div
        ref={ref}
        className={`modal ${wide ? "modal-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="modal-heading">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <IconButton
            label="Close dialog"
            icon={X}
            disabled={!dismissible}
            onClick={onClose}
          />
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}
export function Notice({
  children,
  kind = "info",
}: {
  children: React.ReactNode;
  kind?: "info" | "warning" | "error" | "success";
}) {
  const Icon =
    kind === "error"
      ? AlertCircle
      : kind === "warning"
        ? AlertTriangle
        : kind === "success"
          ? Check
          : Info;
  return (
    <div
      className={`notice ${kind}`}
      role={kind === "error" ? "alert" : undefined}
    >
      <Icon size={16} />
      <div>{children}</div>
    </div>
  );
}
export function Diagnostics({
  items,
  onSelect,
}: {
  items: Diagnostic[];
  onSelect?: (id: string) => void;
}) {
  return (
    <div className="diagnostic-list">
      {items.length === 0 ? (
        <div className="validation-clean">
          <Check size={16} /> No issues found in this validation.
        </div>
      ) : (
        items.map((d, i) => (
          <button
            key={`${d.code}-${i}`}
            className={`diagnostic ${d.severity}`}
            onClick={() => d.objectId && onSelect?.(d.objectId)}
            disabled={!d.objectId || !onSelect}
          >
            <span>
              {d.severity === "error" ? (
                <AlertCircle size={15} />
              ) : d.severity === "warning" ? (
                <AlertTriangle size={15} />
              ) : (
                <Info size={15} />
              )}
            </span>
            <span>
              <strong>{d.message}</strong>
              <small>
                {d.code}
                {d.line ? ` · Line ${d.line}` : ""}
              </small>
            </span>
            {d.objectId && onSelect && <ChevronRight size={14} />}
          </button>
        ))
      )}
    </div>
  );
}
export function LossSummary({ loss }: { loss?: LossReport }) {
  if (!loss) return null;
  return (
    <div className="loss-summary">
      {(
        [
          "preserved",
          "translated",
          "preservedOpaque",
          "lost",
          "unresolved",
        ] as const
      ).map((key) => (
        <details key={key} open={key === "lost" || key === "unresolved"}>
          <summary>
            <span className={`loss-dot ${key}`} />
            {
              {
                preserved: "Preserved",
                translated: "Translated",
                preservedOpaque: "Preserved as opaque",
                lost: "Not transferred",
                unresolved: "Needs a decision",
              }[key]
            }
            <span>{loss[key]?.length || 0}</span>
          </summary>
          {!!loss[key]?.length && (
            <ul>
              {loss[key].map((s, i) => (
                <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>
              ))}
            </ul>
          )}
        </details>
      ))}
    </div>
  );
}
export function EmptyState({
  icon: Icon,
  title,
  children,
  action,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <Icon size={26} />
      </div>
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}
export const modelTypeLabel = (v: string) =>
  (
    ({
      conceptual: "Conceptual",
      logical: "Logical",
      "logical-physical": "Logical + physical",
      physical: "Physical",
    }) as Record<string, string>
  )[v] || v;
export const dialectLabel = (v: string) =>
  (
    ({
      postgresql: "PostgreSQL",
      oracle: "Oracle Database",
      sqlserver: "SQL Server",
      mysql: "MySQL",
      ase: "SAP ASE",
    }) as Record<string, string>
  )[v] || v;
export function downloadText(name: string, text: string, type = "text/plain") {
  const href = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
export function errorText(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}
